import React from 'react';
import { createPortal } from 'react-dom';
import {
  Pencil,
  Plus,
  Copy,
  ArrowRight,
  ListOrdered,
  Archive,
} from 'lucide-react';
import type { KanbanColumn } from 'types/kanban';
import styles from './ListMenu.module.scss';

export type ListMenuView =
  'main' | 'copyList' | 'moveList' | 'moveAllCards' | 'sortBy';

export interface ListMenuAnchor {
  columnId: string;
  rect: DOMRect;
}

export interface ListMenuProps {
  anchor: ListMenuAnchor | null;
  view: ListMenuView;
  columns: KanbanColumn[];
  copyListName: string;
  moveListPosition: number;
  moveAllCardsTargetId: string;
  sortByOption:
    'titleAsc' | 'titleDesc' | 'startAsc' | 'startDesc' | 'dueAsc' | 'dueDesc';
  onClose: () => void;
  onViewChange: (view: ListMenuView) => void;
  onRenameColumn: (columnId: string) => void;
  onListMenuAction: (
    columnId: string,
    action: 'addCard' | 'copyList' | 'moveList' | 'moveAllCards' | 'sortBy',
  ) => void;
  onCopyListCreate: () => void;
  onCopyListNameChange: (value: string) => void;
  onMoveListToPosition: () => void;
  onMoveListPositionChange: (position: number) => void;
  onMoveAllCardsToTarget: () => void;
  onMoveAllCardsTargetIdChange: (id: string) => void;
  onSortListBy: () => void;
  onSortByOptionChange: (
    value:
      | 'titleAsc'
      | 'titleDesc'
      | 'startAsc'
      | 'startDesc'
      | 'dueAsc'
      | 'dueDesc',
  ) => void;
  onArchiveList: (columnId: string) => void;
  onArchiveAllCards: (columnId: string) => void;
}

export default function ListMenu({
  anchor,
  view,
  columns,
  copyListName,
  moveListPosition,
  moveAllCardsTargetId,
  sortByOption,
  onClose,
  onViewChange,
  onRenameColumn,
  onListMenuAction,
  onCopyListCreate,
  onCopyListNameChange,
  onMoveListToPosition,
  onMoveListPositionChange,
  onMoveAllCardsToTarget,
  onMoveAllCardsTargetIdChange,
  onSortListBy,
  onSortByOptionChange,
  onArchiveList,
  onArchiveAllCards,
}: ListMenuProps) {
  if (!anchor) return null;

  const style: React.CSSProperties = {
    position: 'fixed',
    top: anchor.rect.bottom + 4,
    right: Math.max(8, window.innerWidth - anchor.rect.right),
    minWidth: 220,
  };

  return createPortal(
    <>
      <div className={styles.menuBackdrop} onClick={onClose} aria-hidden />
      <div className={styles.menuPanel} style={style} role="menu">
        {view === 'main' ? (
          <>
            <div className={styles.menuSectionLabel}>List actions</div>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                onRenameColumn(anchor.columnId);
                onClose();
              }}
              role="menuitem"
            >
              <Pencil size={14} />
              Rename
            </button>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                onListMenuAction(anchor.columnId, 'addCard');
              }}
              role="menuitem"
            >
              <Plus size={14} />
              Add card
            </button>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                onListMenuAction(anchor.columnId, 'copyList');
              }}
              role="menuitem"
            >
              <Copy size={14} />
              Copy list
            </button>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                onListMenuAction(anchor.columnId, 'moveList');
              }}
              role="menuitem"
            >
              <ArrowRight size={14} />
              Move list
            </button>
            <div className={styles.menuSectionLabel}>Card actions</div>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                onListMenuAction(anchor.columnId, 'moveAllCards');
              }}
              role="menuitem"
            >
              Move all cards to...
            </button>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                onListMenuAction(anchor.columnId, 'sortBy');
              }}
              role="menuitem"
            >
              <ListOrdered size={14} />
              Sort by...
            </button>
            <div className={styles.menuSectionLabel}>Archive actions</div>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                onArchiveList(anchor.columnId);
                onClose();
              }}
              role="menuitem"
            >
              <Archive size={14} />
              Archive list
            </button>
            <button
              type="button"
              className={styles.menuItem}
              onClick={() => {
                onArchiveAllCards(anchor.columnId);
                onClose();
              }}
              role="menuitem"
            >
              <Archive size={14} />
              Archive cards
            </button>
          </>
        ) : (
          <div className={styles.menuBody}>
            {view === 'copyList' && (
              <>
                <div className={styles.menuTitle}>Copy list</div>
                <input
                  type="text"
                  className={styles.menuInput}
                  placeholder="List name"
                  value={copyListName}
                  onChange={(e) => onCopyListNameChange(e.target.value)}
                  aria-label="New list name"
                />
                <div className={styles.menuActions}>
                  <button
                    type="button"
                    className={`${styles.menuBtn} ${styles.menuBtnPrimary}`}
                    onClick={() => void onCopyListCreate()}
                    disabled={!copyListName.trim()}
                  >
                    Create list
                  </button>
                  <button
                    type="button"
                    className={`${styles.menuBtn} ${styles.menuBtnSecondary}`}
                    onClick={() => onViewChange('main')}
                  >
                    Back
                  </button>
                </div>
              </>
            )}
            {view === 'moveList' && (
              <>
                <div className={styles.menuTitle}>Move list</div>
                <label className={styles.menuLabel}>Position</label>
                <select
                  className={styles.menuSelect}
                  value={moveListPosition}
                  onChange={(e) =>
                    onMoveListPositionChange(Number(e.target.value))
                  }
                  aria-label="List position"
                >
                  {columns.map((c, idx) => (
                    <option key={c.id} value={idx}>
                      {idx + 1}. {c.title}
                    </option>
                  ))}
                </select>
                <div className={styles.menuActions}>
                  <button
                    type="button"
                    className={`${styles.menuBtn} ${styles.menuBtnPrimary}`}
                    onClick={() => void onMoveListToPosition()}
                  >
                    Move
                  </button>
                  <button
                    type="button"
                    className={`${styles.menuBtn} ${styles.menuBtnSecondary}`}
                    onClick={() => onViewChange('main')}
                  >
                    Back
                  </button>
                </div>
              </>
            )}
            {view === 'moveAllCards' && (
              <>
                <div className={styles.menuTitle}>Move all cards to...</div>
                <label className={styles.menuLabel}>Target list</label>
                <select
                  className={styles.menuSelect}
                  value={moveAllCardsTargetId}
                  onChange={(e) => onMoveAllCardsTargetIdChange(e.target.value)}
                  aria-label="Target list"
                >
                  <option value="">Select list</option>
                  {columns
                    .filter((c) => c.id !== anchor.columnId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                </select>
                <div className={styles.menuActions}>
                  <button
                    type="button"
                    className={`${styles.menuBtn} ${styles.menuBtnPrimary}`}
                    onClick={() => void onMoveAllCardsToTarget()}
                    disabled={!moveAllCardsTargetId}
                  >
                    Move all cards
                  </button>
                  <button
                    type="button"
                    className={`${styles.menuBtn} ${styles.menuBtnSecondary}`}
                    onClick={() => onViewChange('main')}
                  >
                    Back
                  </button>
                </div>
              </>
            )}
            {view === 'sortBy' && (
              <>
                <div className={styles.menuTitle}>Sort by</div>
                <label className={styles.menuLabel}>Order</label>
                <select
                  className={styles.menuSelect}
                  value={sortByOption}
                  onChange={(e) =>
                    onSortByOptionChange(
                      e.target.value as
                        | 'titleAsc'
                        | 'titleDesc'
                        | 'startAsc'
                        | 'startDesc'
                        | 'dueAsc'
                        | 'dueDesc',
                    )
                  }
                  aria-label="Sort order"
                >
                  <option value="titleAsc">Title A–Z</option>
                  <option value="titleDesc">Title Z–A</option>
                  <option value="startAsc">Start date (earliest first)</option>
                  <option value="startDesc">Start date (latest first)</option>
                  <option value="dueAsc">Due date (earliest first)</option>
                  <option value="dueDesc">Due date (latest first)</option>
                </select>
                <div className={styles.menuActions}>
                  <button
                    type="button"
                    className={`${styles.menuBtn} ${styles.menuBtnPrimary}`}
                    onClick={() => void onSortListBy()}
                  >
                    Sort
                  </button>
                  <button
                    type="button"
                    className={`${styles.menuBtn} ${styles.menuBtnSecondary}`}
                    onClick={() => onViewChange('main')}
                  >
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}
